import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { format } from "date-fns";
import FileUploader from "../components/FileUploader";
import {
  Car, SprayCan, PaintRoller, UserRound, Utensils, Camera,
  Wrench, Truck, Gem, ArrowRight, Calendar as CalIcon,
  Clock as ClockIcon, MapPin, Phone, CheckCircle2,
} from "lucide-react";

const ICONS = {
  detailing: Car, cleaning: SprayCan, "property-maintenance": Wrench,
  chauffeur: UserRound, concierge: Utensils, photography: Camera,
  removals: Truck, catering: Gem,
};

const HERO_IMG = "https://images.unsplash.com/photo-1518791841217-8f162f1e1131?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Book() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [serviceSlug, setServiceSlug] = useState(params.get("service") || "");
  const [packageId, setPackageId] = useState(params.get("package") || "");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [location, setLocation] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [postcode, setPostcode] = useState("");
  const [postcodeInfo, setPostcodeInfo] = useState(null);
  const [postcodeLoading, setPostcodeLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [heard, setHeard] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [agree, setAgree] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/services").then((r) => setServices(r.data)); }, []);
  useEffect(() => {
    if (user) { setName(user.name || ""); setEmail(user.email || ""); setPhone(user.phone || ""); }
  }, [user]);

  const service = services.find((s) => s.slug === serviceSlug);
  const pkg = service?.packages.find((p) => p.id === packageId) || service?.packages?.[0];

  // Constraints for date/time
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const isToday = date === todayStr;
  const nowHM = `${String(today.getHours()).padStart(2, "0")}:${String(today.getMinutes()).padStart(2, "0")}`;
  const minTime = isToday ? (nowHM < "09:00" ? "09:00" : nowHM) : "09:00";
  const maxTime = "21:00";

  const lookupPostcode = async () => {
    const pc = postcode.trim();
    if (!pc) { toast.error("Please enter a postcode"); return; }
    setPostcodeLoading(true);
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`);
      if (!res.ok) throw new Error("Postcode not found");
      const data = await res.json();
      const r = data.result || {};
      setPostcodeInfo(r);
      const areaParts = [r.thoroughfare, r.ward || r.admin_ward, r.admin_district, r.postcode]
        .filter(Boolean);
      const seen = new Set();
      const areaUnique = areaParts.filter((p) => !seen.has(p) && seen.add(p));
      const composed = houseNumber.trim() ? `${houseNumber.trim()}, ${areaUnique.join(", ")}` : areaUnique.join(", ");
      setLocation(composed);
      toast.success("Postcode found — add house/flat number");
    } catch (err) {
      toast.error("Postcode not found — enter address manually");
    } finally {
      setPostcodeLoading(false);
    }
  };

  // When house number changes after a successful postcode lookup, refresh the combined location
  useEffect(() => {
    if (!postcodeInfo) return;
    const r = postcodeInfo;
    const areaParts = [r.thoroughfare, r.ward || r.admin_ward, r.admin_district, r.postcode].filter(Boolean);
    const seen = new Set();
    const areaUnique = areaParts.filter((p) => !seen.has(p) && seen.add(p));
    const composed = houseNumber.trim() ? `${houseNumber.trim()}, ${areaUnique.join(", ")}` : areaUnique.join(", ");
    setLocation(composed);
  }, [houseNumber, postcodeInfo]);

  useEffect(() => {
    if (service && !service.packages.find((p) => p.id === packageId)) {
      setPackageId(service.packages[0]?.id || "");
    }
  }, [service, packageId]);

  const submit = async (payWithCard) => {
    if (!service || !pkg || !date || !location || !name || !email) {
      toast.error("Please complete all required fields");
      return;
    }
    if (!agree) { toast.error("Please accept the Privacy Policy and Terms"); return; }
    setLoading(true);
    try {
      const payload = {
        service_slug: service.slug, package_id: pkg.id,
        date, time: time || "10:00", location, notes,
      };
      if (!user) {
        payload.guest_name = name;
        payload.guest_email = email;
        payload.guest_phone = phone;
      }
      const { data: booking } = await api.post("/bookings", payload);
      if (attachments.length > 0) {
        try {
          await api.patch(`/bookings/${booking.id}/attachments`, { attachments });
        } catch (attErr) {
          // Non-fatal — booking is created, attachments failed
          console.warn("Attachment save failed", attErr);
        }
      }
      if (payWithCard) {
        const { data: co } = await api.post("/payments/checkout", {
          booking_id: booking.id, origin_url: window.location.origin,
        });
        window.location.href = co.url;
        return;
      }
      toast.success("Booking request submitted. Our team will confirm shortly.");
      if (user) nav("/dashboard"); else nav("/");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="book-page" className="bg-[#F5EFE4]">
      <section className="relative pt-32 pb-20 bg-[#1A1815] overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: `url('${HERO_IMG}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1815] via-[#1A1815]/85 to-transparent" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="overline">Your Time. Our Priority.</p>
          <h1 className="font-serif text-5xl lg:text-7xl mt-4 leading-[1.02] text-[#F5EFE4] max-w-2xl">Book a Service</h1>
          <div className="mt-4"><span className="divider-gold-center" /></div>
          <p className="mt-8 text-[#F5EFE4]/70 max-w-lg leading-relaxed">
            Schedule a service or consultation with our team. We are available 7 days a week to assist you.
          </p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl">
            {[["Quick & Easy","Booking"],["Tailored to","Your Needs"],["Discreet &","Professional"],["Available","24/7"]].map(([a, b], i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full border border-[#C5A059] flex items-center justify-center text-[#C5A059]"><CheckCircle2 size={16} /></div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#C5A059] font-medium">{a}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5EFE4] font-medium">{b}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center">
          <p className="overline">Book Your Service</p>
          <h2 className="font-serif text-4xl lg:text-5xl mt-4">Tell Us What You Need</h2>
          <div className="mt-4 flex justify-center"><span className="divider-gold-center" /></div>
          <p className="mt-4 text-[#6B665E]">Please provide the details below and we will confirm your booking.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-16">
          <div className="lg:col-span-8 space-y-14">
            <section>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#1A1815]"><span className="text-[#C5A059]">1.</span>&nbsp; Select a Service</p>
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {services.map((s) => {
                  const Icon = ICONS[s.slug] || Gem;
                  const active = serviceSlug === s.slug;
                  return (
                    <button key={s.slug} onClick={() => setServiceSlug(s.slug)} type="button" data-testid={`book-service-${s.slug}`}
                      className={`relative p-4 bg-white border transition-all text-left ${active ? "border-[#C5A059] shadow-[0_10px_30px_-15px_rgba(197,160,89,0.5)]" : "border-[#E4D9C1] hover:border-[#C5A059]/50"}`}>
                      {active && (<span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#C5A059] flex items-center justify-center text-white"><CheckCircle2 size={12} /></span>)}
                      <div className="w-10 h-10 rounded-full bg-[#F5EFE4] flex items-center justify-center text-[#1A1815] mx-auto"><Icon size={18} strokeWidth={1.5} /></div>
                      <div className="text-center mt-3 text-xs font-medium tracking-wide text-[#1A1815]">{s.name}</div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#1A1815]"><span className="text-[#C5A059]">2.</span>&nbsp; Service Details</p>
              {service && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {service.packages.map((p) => {
                    const u = (p.unit || "").trim();
                    const isFrom = u.toLowerCase().startsWith("from");
                    const isQuote = u.toLowerCase().startsWith("quote");
                    const isComp = u.toLowerCase().startsWith("complimentary");
                    const suffix = isFrom ? u.slice(4).trim().replace(/^·\s*/, "") : u;
                    return (
                      <button key={p.id} type="button" onClick={() => setPackageId(p.id)} data-testid={`book-package-${p.id}`}
                        className={`text-left bg-white border p-5 transition-all ${pkg?.id === p.id ? "border-[#C5A059] shadow-[0_10px_30px_-15px_rgba(197,160,89,0.5)]" : "border-[#E4D9C1] hover:border-[#C5A059]/50"}`}>
                        <div className="font-serif text-xl">{p.name}</div>
                        {isQuote ? (
                          <>
                            <div className="text-[#C5A059] text-2xl font-serif mt-2">Quote</div>
                            <div className="text-[10px] uppercase tracking-[0.24em] text-[#6B665E] mt-0.5">on request</div>
                          </>
                        ) : isComp ? (
                          <>
                            <div className="text-[#C5A059] text-xl font-serif mt-2">Complimentary</div>
                            <div className="text-[10px] uppercase tracking-[0.24em] text-[#6B665E] mt-0.5">for members</div>
                          </>
                        ) : (
                          <>
                            <div className="text-[#C5A059] text-2xl font-serif mt-2">
                              {isFrom && <span className="text-sm text-[#6B665E] mr-1">From</span>}£{p.price.toLocaleString()}
                            </div>
                            {suffix && <div className="text-[10px] uppercase tracking-[0.24em] text-[#6B665E] mt-0.5">{suffix}</div>}
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <div><label className="luxor-label">Preferred Date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} min={todayStr} className="luxor-field" data-testid="book-date" required /></div>
                <div><label className="luxor-label">Preferred Time <span className="text-[#6B665E] normal-case tracking-normal">(9am–9pm)</span></label><input type="time" value={time} onChange={(e) => setTime(e.target.value)} min={minTime} max={maxTime} className="luxor-field" data-testid="book-time" /></div>
                <div className="md:col-span-2">
                  <label className="luxor-label">Postcode</label>
                  <div className="flex gap-2">
                    <input placeholder="e.g. SW1A 1AA" value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupPostcode(); } }} className="luxor-field flex-1" data-testid="book-postcode" />
                    <button type="button" onClick={lookupPostcode} disabled={postcodeLoading} className="luxor-btn-dark shrink-0 disabled:opacity-50" data-testid="book-postcode-lookup">{postcodeLoading ? "Finding…" : "Find Address"}</button>
                  </div>
                  <p className="text-xs text-[#6B665E] mt-1">Enter your UK postcode and click <em>Find Address</em>.</p>
                </div>
                {postcodeInfo && (
                  <div className="md:col-span-2">
                    <label className="luxor-label">Flat / House number / Building name</label>
                    <input placeholder="e.g. Flat 3, 45, The Old Rectory" value={houseNumber} onChange={(e) => setHouseNumber(e.target.value)} className="luxor-field" data-testid="book-house-number" />
                    <p className="text-xs text-[#6B665E] mt-1">
                      Area matched: {postcodeInfo.thoroughfare || postcodeInfo.admin_ward || postcodeInfo.admin_district}, {postcodeInfo.postcode}
                    </p>
                  </div>
                )}
                <div className="md:col-span-2"><label className="luxor-label">Location / Address</label><input placeholder="Address (auto-filled or edit manually)" value={location} onChange={(e) => setLocation(e.target.value)} className="luxor-field" data-testid="book-location" required /></div>
                <div className="md:col-span-2">
                  <label className="luxor-label">Additional Information</label>
                  <textarea rows={4} placeholder="Please provide any details about your request..." value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} className="luxor-field resize-none" data-testid="book-notes" />
                  <div className="text-right text-xs text-[#6B665E] mt-1">{notes.length}/500</div>
                </div>
                <div className="md:col-span-2">
                  <FileUploader
                    folder="bookings/tmp/"
                    value={attachments}
                    onChange={setAttachments}
                    max={5}
                    label="Attach photos or documents (optional)"
                    data-testid="book-uploader"
                  />
                </div>
              </div>
            </section>

            <section>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#1A1815]"><span className="text-[#C5A059]">3.</span>&nbsp; Your Details</p>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><label className="luxor-label">Full Name</label><input placeholder="Enter your full name" value={name} onChange={(e) => setName(e.target.value)} className="luxor-field" data-testid="book-guest-name" required /></div>
                <div><label className="luxor-label">Email Address</label><input type="email" placeholder="Enter your email address" value={email} onChange={(e) => setEmail(e.target.value)} className="luxor-field" data-testid="book-guest-email" required /></div>
                <div><label className="luxor-label">Phone Number</label><input placeholder="Enter your phone number" value={phone} onChange={(e) => setPhone(e.target.value)} className="luxor-field" data-testid="book-guest-phone" /></div>
                <div>
                  <label className="luxor-label">How did you hear about us?</label>
                  <select className="luxor-field" value={heard} onChange={(e) => setHeard(e.target.value)} data-testid="book-heard">
                    <option value="">Select an option</option>
                    <option>Google Search</option><option>Referral</option><option>Social Media</option><option>Other</option>
                  </select>
                </div>
              </div>
              <label className="mt-6 flex items-start gap-3 text-sm text-[#1A1815]">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="book-agree" className="mt-1 accent-[#C5A059]" />
                <span>I agree to the <a className="text-[#C5A059] underline">Privacy Policy</a> and <a className="text-[#C5A059] underline">Terms &amp; Conditions</a></span>
              </label>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button onClick={() => submit(false)} disabled={loading} className="luxor-btn-dark flex-1 justify-center disabled:opacity-50" data-testid="book-request-btn">
                  {loading ? "Submitting…" : "Submit Booking Request"} <ArrowRight size={14} />
                </button>
                <button onClick={() => submit(true)} disabled={loading} className="luxor-btn-primary flex-1 justify-center disabled:opacity-50" data-testid="book-pay-btn">
                  {loading ? "Processing…" : "Pay £50 Deposit"}
                </button>
              </div>
              <p className="mt-5 text-center text-[10px] uppercase tracking-[0.24em] text-[#6B665E]" data-testid="book-payment-methods">
                £50 deposit today · Balance on the day (cash or card) · Full quote provided in advance
              </p>
              <p className="mt-2 text-center text-[10px] uppercase tracking-[0.24em] text-[#6B665E]">
                We accept · Card · Apple Pay · Google Pay · PayPal · Bank Transfer
              </p>
            </section>
          </div>

          <aside className="lg:col-span-4 space-y-6">
            <div className="sticky top-24 space-y-6">
              <div className="bg-[#1A1815] text-[#F5EFE4] p-8">
                <p className="text-[10px] uppercase tracking-[0.24em] text-[#C5A059] font-semibold">Booking Summary</p>
                <div className="mt-3 flex"><span className="divider-gold-center" /></div>
                <div className="mt-8 space-y-6 text-sm">
                  <SumRow icon={CalIcon} label="Service" value={service?.name} sub={pkg?.name} />
                  <SumRow icon={CalIcon} label="Date" value={date ? format(new Date(date), "PP") : "Not selected"} />
                  <SumRow icon={ClockIcon} label="Time" value={time || "Not selected"} />
                  <SumRow icon={MapPin} label="Location" value={location || "Not selected"} />
                </div>
                {pkg && (
                  <div className="border-t border-white/10 mt-8 pt-6 space-y-3">
                    <div className="flex justify-between items-baseline">
                      <span className="text-[10px] uppercase tracking-[0.24em] text-[#C5A059]">Total</span>
                      <span className="font-serif text-3xl text-[#F5EFE4]">
                        {(pkg.unit || "").toLowerCase().startsWith("quote") ? "Quote" :
                          (pkg.unit || "").toLowerCase().startsWith("complimentary") ? "Complimentary" : (
                            <>{(pkg.unit || "").toLowerCase().startsWith("from") && <span className="text-lg text-[#C5A059]/80 mr-1">From</span>}£{pkg.price.toLocaleString()}</>
                          )}
                      </span>
                    </div>
                    {!((pkg.unit || "").toLowerCase().startsWith("quote") || (pkg.unit || "").toLowerCase().startsWith("complimentary")) && (
                      <div className="text-xs text-[#F5EFE4]/70 border-t border-white/10 pt-3 space-y-1" data-testid="booking-deposit-info">
                        <div className="flex justify-between"><span>Deposit today</span><span className="text-[#C5A059] font-medium">£{Math.min(50, pkg.price).toFixed(2)}</span></div>
                        <div className="flex justify-between"><span>Balance on the day</span><span>£{Math.max(pkg.price - 50, 0).toFixed(2)}</span></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="bg-white border border-[#E4D9C1] p-8">
                <p className="text-[10px] uppercase tracking-[0.24em] text-[#1A1815] font-semibold">Need Immediate Assistance?</p>
                <p className="mt-4 text-sm text-[#6B665E] leading-relaxed">Our team is available 24/7 to assist with urgent requests.</p>
                <Link to="/contact" className="luxor-btn-outline-dark mt-6 w-full justify-center" data-testid="book-contact"><Phone size={13} /> Contact Us Now</Link>
              </div>
              <div className="bg-white border border-[#E4D9C1] p-8">
                <p className="text-[10px] uppercase tracking-[0.24em] text-[#1A1815] font-semibold">Why Book With Us?</p>
                <ul className="mt-5 space-y-4">
                  {[["Personalised Service","Tailored to your unique needs"],["Professional & Trusted","Experienced, vetted & insured"],["Discreet & Confidential","Your privacy is our priority"],["Satisfaction Guaranteed","We ensure excellence every time"]].map(([t, s]) => (
                    <li key={t} className="flex gap-3">
                      <CheckCircle2 size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                      <div><div className="text-sm font-medium text-[#1A1815]">{t}</div><div className="text-xs text-[#6B665E]">{s}</div></div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function SumRow({ icon: I, label, value, sub }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-9 h-9 shrink-0 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#C5A059]"><I size={14} /></div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C5A059] font-medium">{label}</div>
        <div className="mt-1 text-[#F5EFE4]/95 text-sm">{value || "Not selected"}</div>
        {sub && <div className="text-xs text-[#F5EFE4]/50 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}
